import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ActionData } from '@bonusly/common-action-input';
import { createBonusData } from '@bonusly/common-give-box';
import { useHttpClient, type HttpResponse } from '@bonusly/http-client';
import { useIdentity } from '@bonusly/who-am-i';

import { Bonus, CachedBonuses } from '../../../../types/bonus';
import { updateCachedBonus } from '../cache/mutators/bonus';
import { QueryKey } from '../hooks/types';
import { useToast } from '../hooks/use-toast';

interface UseEditRecognitionConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
}

export const useEditRecognition = (bonus: Bonus, { onError, onSuccess }: UseEditRecognitionConfig = {}) => {
	const { t } = useTranslation('recognition');
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const { createSuccessToast } = useToast();
	const { refetchProfile } = useIdentity();
	const { mutate, isLoading: isSubmitting } = useMutation<HttpResponse<{ result: Bonus }>, unknown, ActionData, unknown>(
		(action) => {
			const data = {
				bonus: createBonusData({ action }),
			};
			// when patching, if the attachments is missing, the attachment_attributes is not sent
			// which the backend needs so understand to delete any attachments.
			if (!data.bonus.attachments_attributes) {
				data.bonus.attachments_attributes = [];
			}
			return http.patch(`/bonuses/${bonus.id}`, data, {
				params: { include_children: true },
			});
		},
		{
			onError: (err: unknown) => {
				onError?.(err);
			},
			onSuccess: (data) => {
				createSuccessToast(t('edit_bonus.feedback.success'));

				queryClient.invalidateQueries([QueryKey.UserProfile]);
				queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => updateCachedBonus(data.data.result, cached));
				queryClient.invalidateQueries([QueryKey.ProfileBonuses]);
				refetchProfile();

				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
